import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { captureDemandSignal } from "@/services/demandSignalService";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
});

type FormValues = z.infer<typeof schema>;

interface NotifyMeFormProps {
  filterState: Record<string, unknown>;
  /** Offering type this Notify-Me is scoped to (per-type zero-result flow, ADR-088). */
  offeringType?: string;
  onSuccess?: () => void;
  /** When true, the account email is used instead of asking the visitor to type one. */
  isAuthenticated?: boolean;
  /** The authenticated visitor's account email — null/undefined while still loading. */
  accountEmail?: string | null;
  accessToken?: string;
}

export function NotifyMeForm({
  filterState,
  offeringType,
  onSuccess,
  isAuthenticated,
  accountEmail,
  accessToken,
}: NotifyMeFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await captureDemandSignal({ email: values.email, offeringType, filterState });
      setSubmitted(true);
      onSuccess?.();
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const onAuthenticatedSubmit = async () => {
    setIsSubmittingAuth(true);
    try {
      await captureDemandSignal({ filterState, offeringType, accessToken });
      setSubmitted(true);
      onSuccess?.();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmittingAuth(false);
    }
  };

  if (submitted) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex items-center gap-2 text-sm text-[hsl(210,60%,18%)] font-medium"
      >
        <CheckCircle2 className="w-5 h-5 shrink-0" aria-hidden="true" />
        <span>We'll notify you when matching offerings are available!</span>
      </div>
    );
  }

  // Authenticated visitors already have an email on file — skip asking for one
  // entirely (no input field), just a one-click confirm using the account email.
  if (isAuthenticated) {
    const emailKnown = !!accountEmail;
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full max-w-md">
        <span className="text-sm text-muted-foreground">
          {emailKnown ? (
            <>We'll notify <span className="font-medium text-foreground">{accountEmail}</span></>
          ) : (
            "Loading your account details..."
          )}
        </span>
        <Button
          type="button"
          onClick={onAuthenticatedSubmit}
          disabled={!emailKnown || isSubmittingAuth}
          className="rounded-full px-5 cursor-pointer transition-opacity duration-150 hover:opacity-90 shrink-0"
        >
          {isSubmittingAuth ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            "Notify Me"
          )}
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col sm:flex-row gap-2 w-full max-w-md"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder="your@email.com"
                  aria-label="Email address"
                  disabled={form.formState.isSubmitting}
                  className="rounded-full px-4"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="rounded-full px-5 cursor-pointer transition-opacity duration-150 hover:opacity-90 shrink-0"
        >
          {form.formState.isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            "Notify Me"
          )}
        </Button>
      </form>
    </Form>
  );
}
